"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useId, useState } from "react";
import logo from "@/public/images/logo-dark.png";
import Icon from "@/components/Icon";

const LINKS = [
  { href: "#services", label: "Services" },
  { href: "#coverage", label: "Coverage" },
  { href: "#about", label: "About" },
  { href: "#contact", label: "Contact" },
];

/**
 * Sticky top nav. On the homepage links are in-page anchors; elsewhere they
 * point back to the homepage. Below 760px the links collapse into a menu
 * button with a drop-down panel.
 */
export default function SiteNav({ home = false }: { home?: boolean }) {
  const prefix = home ? "" : "/";
  const [open, setOpen] = useState(false);
  const panelId = useId();

  // Close on Escape, and whenever the viewport grows past the mobile breakpoint
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    const mq = window.matchMedia("(min-width: 760px)");
    const onMq = () => { if (mq.matches) setOpen(false); };
    window.addEventListener("keydown", onKey);
    mq.addEventListener("change", onMq);
    return () => { window.removeEventListener("keydown", onKey); mq.removeEventListener("change", onMq); };
  }, [open]);

  const close = () => setOpen(false);

  return (
    <nav className={`nav${open ? " is-open" : ""}`} aria-label="Primary">
      <Link href={home ? "#top" : "/"} className="nav-brand" aria-label="LegioPro home" onClick={close}>
        <Image src={logo} alt="LegioPro" height={32} style={{ height: 32, width: "auto" }} priority />
      </Link>

      <div className="nav-links">
        {LINKS.map((l) => (
          <a key={l.href} href={`${prefix}${l.href}`} className="nav-link">{l.label}</a>
        ))}
        <Link href="/blog" className="nav-link">Blog</Link>
        <a className="btn btn-primary" href={`${prefix}#contact`}>Request a quote</a>
      </div>

      <button
        type="button"
        className="nav-toggle"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={open ? "Close menu" : "Open menu"}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name={open ? "x" : "list"} />
      </button>

      <div id={panelId} className="nav-panel" hidden={!open}>
        {LINKS.map((l) => (
          <a key={l.href} href={`${prefix}${l.href}`} className="nav-panel-link" onClick={close}>{l.label}</a>
        ))}
        <Link href="/blog" className="nav-panel-link" onClick={close}>Blog</Link>
        <a className="btn btn-primary btn-block" href={`${prefix}#contact`} onClick={close}>Request a quote</a>
      </div>
    </nav>
  );
}
